// Shared, framework-agnostic helpers for surfacing public-API
// (`/v1/send`, server-origin) notification fields. String + data only —
// no React, no DOM — so both page UIs (via @reflecto/client-ui) and the
// background/SW toast paths (extension service worker, web SW) can import
// them. The React inline-markdown renderer lives in @reflecto/client-ui.

import type { DecryptedNotification } from "../storage-schema";

// Inline-markdown tokens we support (mirrors the client-ui renderer +
// Android's PublicApiRenderer): bold, italic, inline code, and links.
// Each capture keeps the human-visible text and discards the syntax.
const STRIP_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  // [text](url) → text   (link label only; the URL is dropped for plain toasts).
  // Balanced-paren url scanner so a url with one level of parens (Wikipedia-style
  // `..._(language)`) is consumed in full, not truncated at the first `)`.
  [/\[([^\]]+)\]\([^()]+(?:\([^()]*\)[^()]*)*\)/g, "$1"],
  [/\*\*([^*\n]+)\*\*/g, "$1"], // **bold**
  [/__([^_\n]+)__/g, "$1"], // __bold__
  [/\*([^*\n]+)\*/g, "$1"], // *italic*
  [/_([^_\n]+)_/g, "$1"], // _italic_
  [/`([^`\n]+)`/g, "$1"], // `code`
];

/**
 * Strip inline-markdown syntax to readable plain text. Used for OS-toast
 * surfaces (chrome.notifications, web ServiceWorker showNotification) which
 * render no rich text — so `**Deploy** failed` becomes `Deploy failed`
 * instead of leaking the literal asterisks. Page UIs render the markdown
 * properly via the client-ui renderer instead of calling this.
 *
 * Pure string transform, idempotent on plain text. Only collapses the
 * tokens above; any unmatched `*`/`_`/`` ` `` stays as-is.
 */
export function stripInlineMarkdown(text: string): string {
  let out = text;
  for (const [pattern, replacement] of STRIP_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

/**
 * True when a notification carries a public-API `expiresAt` (derived from
 * the payload `ttl`) that has already passed. Notifications without
 * `expiresAt` (device-mirrored, or ttl=0) never expire.
 */
export function isNotificationExpired(
  notification: Pick<DecryptedNotification, "expiresAt">,
  now: number
): boolean {
  return typeof notification.expiresAt === "number" && notification.expiresAt <= now;
}

/**
 * Drop notifications whose public-API ttl window has elapsed. Hosts call
 * this on load and on an interval so server-origin notifications with a
 * `ttl` auto-dismiss client-side (the server only embeds ttl as a hint; it
 * does not delete on the client's behalf). Returns a new array; preserves
 * order. A no-op for device-mirrored notifications (no `expiresAt`).
 */
export function pruneExpiredNotifications<T extends Pick<DecryptedNotification, "expiresAt">>(
  notifications: readonly T[],
  now: number
): T[] {
  return notifications.filter((n) => !isNotificationExpired(n, now));
}
